import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Lock, CheckCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { redirectToHubLogin } from '@/lib/hub';
import { isAllowedEmail } from '@/config/auth';
import { AuthShell } from './AuthShell';

// Check if hub auth is enabled
const HUB_AUTH_ENABLED = import.meta.env.VITE_HUB_AUTH_ENABLED === 'true';

export function LoginPage() {
  const { user, loading: authLoading, signIn, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [resetSent, setResetSent] = useState(false);

  // If already logged in, redirect to dashboard
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-forest)]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--color-soft-gray)]" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/crm" replace />;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const trimmedEmail = email.trim();

    if (!isAllowedEmail(trimmedEmail)) {
      setError('This email is not authorized to access XYZ HQ.');
      setLoading(false);
      return;
    }

    const { error } = await signIn(trimmedEmail, password);

    if (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const trimmedEmail = email.trim();

    if (!isAllowedEmail(trimmedEmail)) {
      setError('This email is not authorized to access XYZ HQ.');
      setLoading(false);
      return;
    }

    const { error } = await resetPassword(trimmedEmail);

    if (error) {
      setError(error.message);
    } else {
      setResetSent(true);
    }
    setLoading(false);
  };

  if (resetSent) {
    return (
      <AuthShell>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(184,150,62,0.15)]">
              <CheckCircle className="h-6 w-6 text-[var(--color-gold-dark)]" />
            </div>
            <CardTitle className="font-serif">Check your email</CardTitle>
            <CardDescription>
              We sent a password reset link to <strong>{email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Click the link in your email to set a new password.
            </p>
            <Button
              variant="outline"
              className="h-11"
              onClick={() => {
                setResetSent(false);
                setMode('login');
              }}
            >
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(184,150,62,0.15)]">
            <Lock className="h-6 w-6 text-[var(--color-gold-dark)]" />
          </div>
          <CardTitle className="font-serif">
            {mode === 'login' ? 'Sign in to your account' : 'Reset your password'}
          </CardTitle>
          <CardDescription>
            {mode === 'login'
              ? 'Enter your email and password'
              : 'Enter your email to receive a reset link'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={mode === 'login' ? handleLogin : handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="h-11"
              />
            </div>

            {mode === 'login' && (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="h-11"
                />
              </div>
            )}

            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full h-11" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {mode === 'login' ? 'Signing in...' : 'Sending reset link...'}
                </>
              ) : (
                mode === 'login' ? 'Sign in' : 'Send reset link'
              )}
            </Button>

            <div className="text-center">
              {mode === 'login' ? (
                <Button
                  type="button"
                  variant="link"
                  className="text-sm"
                  onClick={() => { setMode('forgot'); setError(null); }}
                >
                  Forgot your password?
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="link"
                  className="text-sm"
                  onClick={() => { setMode('login'); setError(null); }}
                >
                  Back to sign in
                </Button>
              )}
            </div>

            {HUB_AUTH_ENABLED && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11"
                  onClick={() => redirectToHubLogin()}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Sign in with XYZ Strategy Hub
                </Button>
              </>
            )}
          </form>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
